import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import {
	Dtos,
	formatAddress,
	SuretyAccountId,
	SuretyAccountStatus,
	SuretyType,
} from "@inrev/common";
import {
	useFetchAccountBondingLine,
	useFetchAccountLatestIndemnityAgreement,
} from "@portal/api/account";
import {
	useAdminCloseAccount,
	useAdminDeclineAccount,
	useAdminFetchAccountCompanies,
	useAdminFetchLatestSuretyAccountUnderwriting,
	useAdminReopenAccount,
	useAdminUnderwrite,
} from "@portal/api/account/admin";
import {
	useCloseAccount,
	useGetAccountIndemnityAgreementFileUrl,
	useReopenAccount,
} from "@portal/api/account/agent";
import { useAdminFetchAgency } from "@portal/api/agency/admin";
import { useAccountFollowers } from "@portal/api/followers";
import { UseFollowersReturn } from "@portal/api/followers/types";
import { useFetchAgentPreviews } from "@portal/api/user/agent";
import { EntityTags } from "@portal/components/admin/EntityTags";
import {
	AgentBondRequestSubmissionAdapterProvider,
	BondRequestSubmissionModal,
} from "@portal/components/features/bond-request-submission";
import { useLogDecisionNudge } from "@portal/components/features/decisioning";
import { FollowerAvatarGroup } from "@portal/components/features/followers/FollowerAvatarGroup";
import { ConfirmationModal } from "@portal/components/layout/ConfirmationModal";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Avatar, Button, ButtonDropdown, Icon, Text } from "@portal/components/primitives";
import { ManageFollowersModal } from "@portal/components/ui/FollowerControls";
import { MutationErrorFlag } from "@portal/components/ui/MutationErrorFlag";
import { getStatusTagByStatus } from "@portal/components/ui/StatusTag";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatEmpty, noop } from "../../../utils";
import { AdminAccountStatusTransitionDropdown } from "../../admin/account/shared/AdminAccountStatusTransitionDropdown";
import { AdminAssigneeInlineControls } from "../../admin/shared/AdminAssigneeInlineControls";
import { AdminFollowerControls } from "../../admin/shared/AdminFollowerControls";
import { AccountCompaniesModal } from "../../agent/account/status/submitted/underwritten/AccountCompaniesModal";
import { BrokerOfRecordAccountModal } from "./BrokerOfRecordAccountModal";
import { ClosedReasonModal, type ClosedReasonModalProps } from "./ClosedReasonModal";

// Text trims the line box by default, which clips descenders on a wrapping name.
const accountDisplayNameClassName = "inline-flex min-w-0 break-all [text-box-trim:none]";

export type AccountIdentity = Pick<
	Dtos.Admin.Account.Get.Response,
	"id" | "agencyId" | "status" | "displayName" | "primaryCompanyId" | "closedReason" | "closedNote"
> & {
	underwriterUserId?: Dtos.Admin.Account.Get.Response["underwriterUserId"];
	underwriter?: Dtos.SuretyAccount.Get.Response["underwriter"];
};

export type AccountHeaderProps = {
	account: AccountIdentity;
	companies?: (Dtos.Company.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Company)[];
	isAdmin?: boolean;
	useCloseAccount: (
		accountId: SuretyAccountId,
	) => ReturnType<typeof useCloseAccount> | ReturnType<typeof useAdminCloseAccount>;
	useReopenAccount: (
		accountId: SuretyAccountId,
	) => ReturnType<typeof useReopenAccount> | ReturnType<typeof useAdminReopenAccount>;
	generateReport?: () => void;
	generateReportIsPending?: boolean;
};

export type AdminAccountHeaderProps = Omit<
	AccountHeaderProps,
	| "companies"
	| "isAdmin"
	| "useCloseAccount"
	| "useReopenAccount"
	| "generateReport"
	| "generateReportIsPending"
> & {
	setClosedReasonModalOpen: (open: boolean) => void;
	setReopenModalOpen: (open: boolean) => void;
	followersControl: UseFollowersReturn;
	isExtractionEnabled: boolean;
	siteHeaderExtractionEnabled: boolean;
	openSubmissionModal: () => void;
	generateReport: () => void;
	generateReportIsPending: boolean;
};

export type AgentAccountHeaderProps = Omit<
	AccountHeaderProps,
	"isAdmin" | "useCloseAccount" | "useReopenAccount" | "generateReport" | "generateReportIsPending"
> & {
	setClosedReasonModalOpen: (open: boolean) => void;
	setReopenModalOpen: (open: boolean) => void;
	followersControl: UseFollowersReturn;
	isExtractionEnabled: boolean;
	openSubmissionModal: () => void;
};

const AdminAccountHeader: FC<AdminAccountHeaderProps> = ({
	account,
	setClosedReasonModalOpen,
	setReopenModalOpen,
	followersControl,
	isExtractionEnabled,
	siteHeaderExtractionEnabled,
	openSubmissionModal,
	generateReport,
	generateReportIsPending,
}) => {
	const { companies } = useAdminFetchAccountCompanies(account.id);
	const primary = companies?.find((company) => company.id === account.primaryCompanyId);
	const { agency, agencyLoading } = useAdminFetchAgency(account.agencyId);
	const { updateUnderwriting, updateUnderwritingIsLoading } = useAdminUnderwrite(
		account.id,
		SuretyType.contract,
	);
	const { latestUnderwriting, latestUnderwritingError } =
		useAdminFetchLatestSuretyAccountUnderwriting(account.id);
	const [declineModalOpen, setDeclineModalOpen] = useState(false);
	const [brokerOfRecordModalOpen, setBrokerOfRecordModalOpen] = useState(false);
	const [runModelConfirmOpen, setRunModelConfirmOpen] = useState(false);
	const canDecline = Boolean(latestUnderwriting?.id) && !latestUnderwritingError;
	const { declineAccount, declineAccountLoading, declineAccountError, dismissDeclineAccountError } =
		useAdminDeclineAccount(account.id, latestUnderwriting?.id);
	const nudgeLogDecision = useLogDecisionNudge();

	return (
		<>
			<div data-testid="admin-account-header" className="flex min-w-0 flex-1 flex-col gap-3">
				<Text
					type="display"
					data-amp-mask
					data-sentry-mask
					data-testid="account-display-name"
					className={accountDisplayNameClassName}
				>
					{account.displayName}
				</Text>
				<div
					data-amp-mask
					data-sentry-mask
					data-testid="account-primary-address"
					className="flex items-center gap-2.5"
				>
					<Icon type="map-pin" size="micro" color="tertiary" className="shrink-0" />
					<Text type="caption" color="tertiary">
						{primary ? formatEmpty(formatAddress(primary.address)) : ""}
					</Text>
				</div>
				<div
					data-testid="account-agency"
					title="Broker of Record"
					className="flex items-center gap-2.5"
				>
					<Icon type="user-group" size="micro" color="tertiary" className="shrink-0" />
					<Text type="caption" color="tertiary">
						{agencyLoading ? "Loading..." : agency?.displayName}
					</Text>
				</div>
				<div
					data-testid="account-assignee-follower-controls"
					className="flex flex-wrap items-center gap-x-6 gap-y-2"
				>
					{account.underwriterUserId !== undefined && (
						<AdminAssigneeInlineControls
							className="pt-0"
							avatarClassName="bg-surface-neutral"
							assignedEntity={{ entityType: "account", entityId: account.id }}
							currentAssignee={account.underwriterUserId}
							trackingId="account-admin-assign-underwriter"
						/>
					)}
					<AdminFollowerControls
						{...followersControl}
						appearance="avatars"
						className="pt-0"
						manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this account"
						agencyId={account.agencyId}
						trackingPrefix="account-admin-header-follow"
					/>
				</div>
				<EntityTags entityType="account" entityId={account.id} />
			</div>
			<div data-testid="account-status" className="grid w-max shrink-0 grid-cols-1 gap-2.5 pt-5">
				<>
					<AdminAccountStatusTransitionDropdown
						currentStatus={account.status}
						canDecline={canDecline}
						onStatusChange={(newStatus) => {
							if (newStatus === SuretyAccountStatus.closed) {
								setClosedReasonModalOpen(true);
							} else if (newStatus === SuretyAccountStatus.declined) {
								if (canDecline) {
									setDeclineModalOpen(true);
								}
							} else {
								setReopenModalOpen(true);
							}
						}}
						trackingPrefix="account-admin-status"
					/>
					{account.status === SuretyAccountStatus.closed && (
						<div
							className="w-0 min-w-full break-all pr-1 text-center text-[13px] text-tertiary italic"
							title={
								account.closedNote ? `${account.closedReason} (${account.closedNote})` : undefined
							}
						>
							{account.closedReason}
							{account.closedNote && ` (${account.closedNote})`}
						</div>
					)}
				</>
				{isExtractionEnabled && !siteHeaderExtractionEnabled && (
					<Button
						color="primary"
						data-testid="admin-request-bond-button"
						variant="outline"
						onClick={openSubmissionModal}
						trackingId="account-admin-request-bond"
					>
						Request a Bond
					</Button>
				)}
				<ButtonDropdown
					variant="outline"
					color="secondary"
					className="w-auto"
					label={<Text type="caption">Run Model</Text>}
					leadingIcon={<Icon type="arrow-path" color="secondary" size="mini" />}
					onClick={() => setRunModelConfirmOpen(true)}
					loading={updateUnderwritingIsLoading}
					trackingId="account-admin-run-model"
					data-testid="more-actions-button"
				>
					<ButtonDropdown.Item
						leadingIcon={<Icon type="document-text" size="mini" />}
						disabled={generateReportIsPending}
						onClick={generateReport}
						trackingId="account-admin-generate-report"
					>
						Generate inRev Report
					</ButtonDropdown.Item>
					<ButtonDropdown.Item
						leadingIcon={<Icon type="user-group" size="mini" />}
						onClick={() => setBrokerOfRecordModalOpen(true)}
						trackingId="account-admin-change-bor"
					>
						Change Broker of Record
					</ButtonDropdown.Item>
				</ButtonDropdown>
			</div>
			{runModelConfirmOpen && (
				<ConfirmationModal
					message="Are you sure you want to run the model?"
					onConfirm={() => {
						updateUnderwriting();
						setRunModelConfirmOpen(false);
					}}
					onCancel={() => setRunModelConfirmOpen(false)}
				/>
			)}
			{declineModalOpen && (
				<ConfirmationModal
					message="Are you sure you want to decline this account?"
					onConfirm={() => {
						declineAccount(undefined, { onSuccess: () => nudgeLogDecision("decline") });
						setDeclineModalOpen(false);
					}}
					onCancel={() => setDeclineModalOpen(false)}
				/>
			)}
			{declineAccountLoading && <LoadingModal />}
			<MutationErrorFlag
				mutationError={declineAccountError}
				dismissError={dismissDeclineAccountError}
			/>
			{brokerOfRecordModalOpen && (
				<BrokerOfRecordAccountModal
					accountId={account.id}
					currentAgencyId={account.agencyId}
					onClose={() => setBrokerOfRecordModalOpen(false)}
				/>
			)}
		</>
	);
};

const AgentAccountHeader: FC<AgentAccountHeaderProps> = ({
	account,
	companies,
	setClosedReasonModalOpen,
	setReopenModalOpen,
	followersControl,
	isExtractionEnabled,
	openSubmissionModal,
}) => {
	const navigate = useNavigate();
	const { latestIndemnityAgreement } = useFetchAccountLatestIndemnityAgreement(account.id);
	const { bondingLine } = useFetchAccountBondingLine(account.id);
	const isBondingLineAvailable =
		Boolean(bondingLine) &&
		bondingLine?.singleLimit !== undefined &&
		bondingLine?.aggregateLimit !== undefined;
	const [companiesModalOpen, setCompaniesModalOpen] = useState(false);
	const [manageFollowersOpen, setManageFollowersOpen] = useState(false);
	const underwriter = account.underwriter;
	const showPrequalButton =
		isBondingLineAvailable && (account.status === "approved" || account.status === "active");
	const { downloadIndemnityAgreement } = useGetAccountIndemnityAgreementFileUrl({
		accountId: account.id,
		asDownload: true,
	});

	const primary = companies?.find((company) => company.id === account.primaryCompanyId);

	const handleHeaderRequestButtonClick = () => {
		if (isExtractionEnabled) {
			openSubmissionModal();
		} else {
			navigate(`/requests/create?accountId=${account.id}`);
		}
	};

	return (
		<>
			<div
				data-testid="account-status-and-followers"
				className="flex min-w-0 flex-1 flex-col gap-4"
			>
				{getStatusTagByStatus(account.status)}
				<Text
					type="display"
					data-amp-mask
					data-sentry-mask
					data-testid="account-display-name"
					className={accountDisplayNameClassName}
				>
					{account.displayName}
				</Text>
				<div
					data-amp-mask
					data-sentry-mask
					data-testid="account-primary-address"
					className="flex items-center gap-2.5"
				>
					<Icon type="map-pin" size="micro" color="tertiary" />
					<Text type="caption" color="tertiary">
						{primary ? formatEmpty(formatAddress(primary.address)) : ""}
					</Text>
				</div>
				<div className="flex flex-wrap items-center gap-x-6 gap-y-2">
					<div className="flex items-center gap-2">
						<Text type="caption" color="secondary">
							Underwriter
						</Text>
						<Avatar
							data-testid="agent-underwriter"
							className="bg-surface-neutral"
							firstName={underwriter?.firstName}
							lastName={underwriter?.lastName}
							aria-label={
								underwriter?.firstName
									? `Underwriter ${underwriter.firstName} ${underwriter.lastName ?? ""}`
									: "Underwriter"
							}
						/>
					</div>
					<div data-testid="account-header-followers" className="flex items-center gap-2">
						<Text type="caption" color="secondary">
							Agent Followers
						</Text>
						<FollowerAvatarGroup
							followers={followersControl.followers ?? []}
							onAdd={() => setManageFollowersOpen(true)}
							addLabel="Add follower"
						/>
					</div>
				</div>
			</div>
			<div
				data-testid="account-actions"
				className="flex w-[148px] max-w-[148px] shrink-0 flex-col gap-2.5 pt-5"
			>
				<Button
					color="primary"
					data-testid="agent-request-bond-button"
					onClick={handleHeaderRequestButtonClick}
					trackingId="account-agent-request-bond"
					className="w-full"
				>
					<Text type="body-strong" className="inline-flex">
						Request a Bond
					</Text>
				</Button>
				<Menu as="div" className="w-full">
					<MenuButton
						as={Button}
						variant="outline"
						color="secondary"
						className="w-full"
						trackingId="account-agent-more-actions"
						data-testid="more-actions-button"
					>
						<span className="inline-flex items-center gap-3">
							<Text type="body-strong" className="inline-flex">
								More actions
							</Text>
							<Icon type="chevron-down" size="mini" />
						</span>
					</MenuButton>
					<MenuItems
						anchor={{ to: "bottom end", gap: 6, padding: 8 }}
						modal={false}
						className="z-[200000] flex w-max flex-col gap-[2px] rounded-md border border-bc-default bg-surface-base p-[4px] shadow-md"
					>
						{latestIndemnityAgreement?.status === "complete" && (
							<ButtonDropdown.Item
								leadingIcon={<Icon type="arrow-down-tray" size="mini" />}
								onClick={() => downloadIndemnityAgreement()}
								trackingId="account-agent-download-indemnity"
							>
								Download Indemnity Agreement
							</ButtonDropdown.Item>
						)}
						{showPrequalButton && (
							<ButtonDropdown.Item
								leadingIcon={<Icon type="arrow-down-tray" size="mini" />}
								onClick={() => setCompaniesModalOpen(true)}
								trackingId="account-agent-prequal-letter"
							>
								Prequal Letter
							</ButtonDropdown.Item>
						)}
						{account.status === "closed" ? (
							<ButtonDropdown.Item
								leadingIcon={<Icon type="folder-open" size="mini" />}
								onClick={() => setReopenModalOpen(true)}
								trackingId="account-agent-reopen"
							>
								Reopen Account
							</ButtonDropdown.Item>
						) : (
							<ButtonDropdown.Item
								leadingIcon={<Icon type="minus-circle" size="mini" />}
								onClick={() => setClosedReasonModalOpen(true)}
								trackingId="account-agent-close"
							>
								Close Account
							</ButtonDropdown.Item>
						)}
					</MenuItems>
				</Menu>
			</div>
			{manageFollowersOpen && (
				<ManageFollowersModal
					{...followersControl}
					manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this account"
					useFetchAgentPreviews={useFetchAgentPreviews}
					onClose={() => setManageFollowersOpen(false)}
				/>
			)}
			{companiesModalOpen && (
				<AccountCompaniesModal
					accountId={account.id}
					companies={companies ?? []}
					onClose={() => setCompaniesModalOpen(false)}
					options={{
						showPrequalButton,
						enablePrequalButton: isBondingLineAvailable,
					}}
				/>
			)}
		</>
	);
};

const AdminCloseAccountModal: FC<ClosedReasonModalProps> = (props) => {
	const nudgeLogDecision = useLogDecisionNudge();
	return (
		<ClosedReasonModal
			{...props}
			onSuccess={() => {
				props.onDismiss();
				nudgeLogDecision("close");
			}}
		/>
	);
};

export const AccountHeader: FC<AccountHeaderProps> = ({
	account,
	companies,
	isAdmin,
	useCloseAccount,
	useReopenAccount,
	generateReport,
	generateReportIsPending,
}) => {
	const [closedReasonModalOpen, setClosedReasonModalOpen] = useState(false);
	const [reopenModalOpen, setReopenModalOpen] = useState(false);
	const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
	const { closeAccount, closeAccountLoading, closeAccountError } = useCloseAccount(account.id);
	const { reopenAccount, reopenAccountLoading, reopenAccountError, dismissReopenAccountError } =
		useReopenAccount(account.id);
	const followersControl = useAccountFollowers(account.id, isAdmin);
	const isExtractionEnabled = useFeatureFlag("bond_request_extraction");
	const aiExtractionEnabled = useFeatureFlag("ai_bond_request_extraction");
	const siteHeaderExtractionEnabled = isExtractionEnabled && aiExtractionEnabled;
	const closeModalProps: ClosedReasonModalProps = {
		closeFunction: closeAccount,
		closeFunctionError: closeAccountError,
		onDismiss: () => setClosedReasonModalOpen(false),
		isClosing: closeAccountLoading,
		closingAction: "close",
		isAccount: true,
	};
	return (
		<>
			<div className="flex w-full space-x-[40px] items-start">
				{isAdmin ? (
					<AdminAccountHeader
						account={account}
						setClosedReasonModalOpen={setClosedReasonModalOpen}
						setReopenModalOpen={setReopenModalOpen}
						followersControl={followersControl}
						isExtractionEnabled={isExtractionEnabled}
						siteHeaderExtractionEnabled={siteHeaderExtractionEnabled}
						openSubmissionModal={() => setSubmissionModalOpen(true)}
						generateReport={generateReport ?? noop}
						generateReportIsPending={generateReportIsPending ?? false}
					/>
				) : (
					<AgentAccountHeader
						account={account}
						companies={companies}
						setClosedReasonModalOpen={setClosedReasonModalOpen}
						setReopenModalOpen={setReopenModalOpen}
						followersControl={followersControl}
						isExtractionEnabled={isExtractionEnabled}
						openSubmissionModal={() => setSubmissionModalOpen(true)}
					/>
				)}
			</div>
			{closedReasonModalOpen &&
				(isAdmin ? (
					<AdminCloseAccountModal {...closeModalProps} />
				) : (
					<ClosedReasonModal {...closeModalProps} />
				))}
			{reopenModalOpen && (
				<ConfirmationModal
					message="Are you sure you want to reopen this account?"
					onConfirm={() => {
						reopenAccount();
						setReopenModalOpen(false);
					}}
					onCancel={() => setReopenModalOpen(false)}
				/>
			)}
			{(reopenAccountLoading || closeAccountLoading) && <LoadingModal />}
			<MutationErrorFlag
				mutationError={reopenAccountError}
				dismissError={dismissReopenAccountError}
			/>
			{submissionModalOpen &&
				(isAdmin ? (
					<BondRequestSubmissionModal
						isAdmin={isAdmin}
						accountId={account.id}
						agencyId={account.agencyId}
						onClose={() => setSubmissionModalOpen(false)}
					/>
				) : (
					<AgentBondRequestSubmissionAdapterProvider accountId={account.id}>
						<BondRequestSubmissionModal
							isAdmin={isAdmin}
							accountId={account.id}
							onClose={() => setSubmissionModalOpen(false)}
						/>
					</AgentBondRequestSubmissionAdapterProvider>
				))}
		</>
	);
};
